<template>
  <div class="auth-app-footer">
    <button
      :disabled="isLoading"
      style="position: relative"
      class="btn btn-secondary btn-lg btn-block"
      @click="handleLogin"
    >
      <span class="text" :class="{'btn-hide': isLoading}">
        <IconPasskey />
        {{ $t('authentication-app.buttons.login-using-a-passkey') }}
      </span>
      <span v-if="isLoading" class="loading-spinner"></span>
    </button>
  </div>
</template>

<script setup>
import AuthService from '../../../services/auth';
import { InvalidCredentialIdError } from '../../../models/auth-error';
import IconPasskey from '@assets/images/icons/passkeys.svg?inline';
import { ref } from 'vue';
import { captureException } from '../../../../../frontend-common-lib/src/plugins/sentry';
import { sendGaEvent } from "@/services/dataLayerGaService";


const isLoading = ref(false);
const emit = defineEmits(['success-otp', 'invalid-credential-id-error', 'critical-error']);

async function handleLogin() {
  isLoading.value = true;
  sendGaEvent('passkey_auth_initiated');
  try {
    const tokens = await AuthService.loginUsingPasskey();
    
    sendGaEvent('passkey_auth_validated');
    emit('success-otp', tokens);
  } catch (error) {
    isLoading.value = false;
    if (error.name === 'NotAllowedError') {
      return;
    } else if (error instanceof InvalidCredentialIdError) {
      emit('invalid-credential-id-error', error);
      return;
    }

    captureException({
      error,
    });

    emit('critical-error', error);
  }
}
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/variables';
@import '../../../assets/scss/btn';

.btn .text {
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    margin-right: 5px;
  }

  &.btn-hide {
    opacity: 0;
  }
}

.loading-spinner {
  min-width: 20px;
  min-height: 20px;
  left: 50%;
  top: 50%;
  margin-left: -10px;
  margin-top: -10px;
  position: absolute;

  &:after {
    min-width: inherit;
    min-height: inherit;
    display: block;
    content: '';
    background-image: url('data:image/png;base64,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');
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
    animation: spin 2s linear infinite;
  }

  @media screen and (max-width: $screen-md-max) {
    position: unset;
    right: unset;
    display: inline-flex;
  }
}

@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

.btn-hide {
  @media screen and (max-width: $screen-md-max) {
    display: none;
  }
}
</style>
